import React, { useState, useEffect } from 'react';
import { Shield, Settings, Users, Save, RefreshCw, Check, X, TrendingUp, RotateCcw, Edit2, BarChart3, Eye, Code, Building2, UserRoundCog } from 'lucide-react';
import Layout from '../../components/Layout';
import Button from '../../components/Button';
import CEODashboard from './components/CEODashboard';
import UnifiedAnalyticsDashboard from './components/UnifiedAnalyticsDashboard';
import { EnterpriseDashboard } from './enterprise';
import { FreeTierDashboard } from './freeTier';

const API_URL = import.meta.env.VITE_API_URL || '';

// Constants for build audience prompts
export const CREATE_PERSONA_PROMPT = 'create-persona';
export const EXPERT_REFLECTION_PROMPT = 'expert-reflection';
export const PERPLEXITY_PROMPT = 'perplexity';

// Group of build audience prompts for UI organization
const BUILD_AUDIENCE_PROMPTS = [CREATE_PERSONA_PROMPT, EXPERT_REFLECTION_PROMPT, PERPLEXITY_PROMPT];

// Map of constant names to actual keys
const PROMPT_CONSTANTS_MAP = {
  'CREATE_PERSONA_PROMPT': CREATE_PERSONA_PROMPT,
  'EXPERT_REFLECTION_PROMPT': EXPERT_REFLECTION_PROMPT,
  'PERPLEXITY_PROMPT': PERPLEXITY_PROMPT
};

interface User {
  id: number;
  email: string;
  is_admin: boolean;
  subscription_plan: string;
  subscription_status: string;
  monthly_simulation_count: number;
  simulations_limit: number;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  trial_end_date: string | null;
  total_audience_requests: number;
}

interface SelectedModels {
  claude: string;
  openai: string;
  gemini: string;
  deepseek: string;
  groq: string;
}

interface PromptConfig {
  DEEP_RESEARCH: boolean;
  MEMORY: boolean;
  // attribution: string[];
  'create-content': string[];
  'create-content-long': string[];
  'a/b-multivariate-tests': string[];
  'packaging-review-gemini': string[];
  // 'get-insights': string[];
  'survey_and_focus_groups': string[];
  'create_campaign_strategy': string[];
  'test_use_case': string[];
  'logo_test': string[];
  // 'pricing-analysis': string[];
  'channel-event-strategy': string[];
  'image-survey': string[];
  'creative-review': string[];
  // 'ab-test-creatives-gemini': string[];
  // 'sales-enablement': string[];
  'buyer-insights-report': string[];
  'buyer-insights-report-b2b': string[];
  'buyer-insights-report-b2c': string[];
  'amazon-search': string[];
  'create-persona': string; // Single string prompt
  'expert-reflection': string; // Single string prompt
  'perplexity': string; // Single string prompt
  // Healthcare prompts
  'healthcare-selection-prompt': string;
  'healthcare-system-prompt': string;
  'healthcare-query-prompt': string;
  'healthcare-consolidation-prompt': string;
  // Natural Organic Shoppers prompts
  'natural-organic-system-prompt': string;
  'natural-organic-query-prompt': string;
  'natural-organic-consolidation-prompt': string;
}

// Define tasks that use the three-phase agent pattern
const THREE_PHASE_TASKS = [
  'test_use_case',
  'buyer-insights-report',
  'buyer-insights-report-b2b',
  'buyer-insights-report-b2c',
  // Add more tasks as they are converted to use GenericThreePhaseAgent
];

// Define available variables for each prompt type
const PROMPT_VARIABLES = {
  'test_use_case': {
    phase1: [ // Research Phase Variables
      { name: 'image', description: 'The uploaded image for analysis (automatically included)' },
      { name: 'image_description', description: 'Description of what the image shows' },
      { name: 'context', description: 'User-provided context about the analysis' },
      { name: 'goal', description: 'The objective of the simulation' },
      { name: 'industry', description: 'Industry/domain (if provided)' },
      { name: 'business_description', description: 'Business description (if provided)' },
      { name: 'website', description: 'Website URL (if provided)' },
      { name: 'company_name', description: 'Company name (if provided)' },
      { name: 'creative_type', description: 'Type of creative (if provided)' },
      { name: 'placement_goal', description: 'Placement goal (if provided)' },
      { name: 'campaign_objective', description: 'Campaign objective (if provided)' }
    ],
    phase2: [ // Persona Reaction Phase Variables (includes all phase1 plus:)
      { name: 'image', description: 'The uploaded image (automatically included)' },
      { name: 'image_description', description: 'Description of what the image shows' },
      { name: 'context', description: 'User-provided context' },
      { name: 'goal', description: 'The objective of the simulation' },
      { name: 'research_findings', description: '⭐ Findings from the research phase' },
      { name: 'research_context', description: '⭐ Additional context from research (as JSON)' },
      { name: 'persona_name', description: '👤 Name of the current persona being evaluated' },
      { name: 'persona_id', description: '👤 Unique ID of the current persona' },
      { name: 'segment_name', description: '👤 Segment this persona belongs to' },
      { name: 'persona_data', description: '👤 Full persona profile data as JSON string' }
    ],
    phase3: [ // Summary Phase Variables (includes all previous plus:)
      { name: 'image', description: 'The uploaded image (automatically included)' },
      { name: 'image_description', description: 'Description of what the image shows' },
      { name: 'context', description: 'User-provided context' },
      { name: 'goal', description: 'The objective of the simulation' },
      { name: 'research_findings', description: 'Findings from the research phase' },
      { name: 'research_context', description: 'Research context as JSON' },
      { name: 'total_personas', description: '📊 Total number of personas evaluated' },
      { name: 'persona_responses_json', description: '📊 All persona responses as JSON array' },
      { name: 'persona_table', description: '📊 Pre-formatted markdown table of all persona reactions' }
    ]
  },
  'amazon-search': [
    { name: 'search_term', description: 'The search term used to find Amazon products' },
    { name: 'selected_products', description: 'Array of selected product ASINs for analysis' },
    { name: 'products_data', description: 'Detailed data of selected Amazon products' },
    { name: 'goal', description: 'The objective of the Amazon competitor analysis' },
    { name: 'context', description: 'Background information about the search and analysis' },
    { name: 'competitor_analysis', description: 'Flag indicating this is a competitor analysis' }
  ],
  'channel-event-strategy': [
    { name: 'goal', description: 'The objective of the A/B test' },
    { name: 'image_descriptions', description: 'Descriptions of the images being tested' }
  ],
  'image-survey': [
    { name: 'goal', description: 'The objective of the image survey' },
    { name: 'image_descriptions', description: 'Descriptions of the images being tested' }
  ],
  'creative-review': [
    { name: 'image_descriptions', description: 'Descriptions of the creative images being reviewed' },
    { name: 'campaign_objective', description: 'The specific campaign objective' },
    { name: 'kpis', description: 'Key Performance Indicators for the campaign' },
    { name: 'company_details', description: 'Information about the company' },
    { name: 'product_details', description: 'Details about the product or service' },
    { name: 'competitive_landscape', description: 'Analysis of the competitive environment' },
    { name: 'medium_and_placement', description: 'Where and how the creatives will be used' },
    { name: 'cultural_considerations', description: 'Cultural or regional considerations for the target audience (optional)' },
    { name: 'prior_performance_data', description: 'Historical performance data from previous campaigns' }
  ],
  // 'ab-test-creatives-gemini': [
  //   { name: 'goal', description: 'The objective of the A/B test' },
  // ],
  'packaging-review-gemini': [
    { name: 'goal', description: 'The objective of the packaging review' },
  ],
  'attribution': [
    { name: 'objective', description: 'The goal of the attribution model' },
    { name: 'attribution_mode', description: 'The type of attribution model to use' },
    { name: 'funnel_stages', description: 'The stages in the conversion funnel' }
  ],
  'buyer-insights-report': [
    { name: 'objective', description: 'The goal of the buyer insights report' },
    { name: 'context', description: 'Background information for the report' },
    { name: 'additional_data', description: 'JSON containing product_name and website_url' }
  ],
  
   'buyer-insights-report-b2b': [
    { name: 'objective', description: 'The goal of the buyer insights report' },
    { name: 'context', description: 'Background information for the report' },
    { name: 'additional_data', description: 'JSON containing product_name and website_url' }
  ],
  'buyer-insights-report-b2c': [
    { name: 'objective', description: 'The goal of the buyer insights report' },
    { name: 'context', description: 'Background information for the report' },
    { name: 'additional_data', description: 'JSON containing product_name and website_url' }
  ],
  

  'create-content': [
    { name: 'goal', description: 'The purpose of the content' },
    { name: 'context', description: 'Background information for the content' },
    { name: 'content_type', description: 'The type of content to create' },
    { name: 'content_subject', description: 'The subject matter of the content' },
    { name: 'company_context', description: 'Information about the company' },

  ],
  'a/b-multivariate-tests': [
    { name: 'objective', description: 'The goal of the test' },
    { name: 'test_type', description: 'The type of test (A/B, multivariate)' },
    { name: 'input_messages', description: 'Messages to be tested' }
  ],
  'get-insights': [
    { name: 'questions', description: 'Specific questions to answer' },
    { name: 'context', description: 'Background information for the insights' }
  ],
  'pricing-analysis': [
    { name: 'objective', description: 'The goal of the pricing analysis' },
    { name: 'price_tiers', description: 'Different pricing tiers to analyze' },
    { name: 'product_description', description: 'Description of the product' },
    { name: 'value_proposition', description: 'The value proposition of the product' }
  ],
  'create-content-long': [
    { name: 'goal', description: 'The purpose of the long content' },
    { name: 'context', description: 'Background information for the content' },
    { name: 'content_type', description: 'The type of content to create' },
    { name: 'content_subject', description: 'The subject matter of the content' },
    { name: 'company_context', description: 'Information about the company' },
  ],
  'survey_and_focus_groups': [
    { name: 'goal', description: 'The primary objective or research aim of the survey or focus group' },
    { name: 'questions', description: 'Survey questions (with up to 4 answer choices) or open-ended focus group questions' },
    { name: 'context', description: 'Additional background information (product/service details, market environment, specific instructions)' },
    { name: 'personas', description: 'Detailed list of AI persona profiles including name, role, demographics, background, values, motivations, goals, pain points and thinking style' }
  ],
  'create_campaign_strategy': [
    { name: 'goal', description: 'The primary objective of the campaign (e.g., +5% market share in 6 months)' },
    { name: 'product', description: 'The name of the product for the campaign' },
    { name: 'product_website', description: 'The website URL for the product' },
    { name: 'budget', description: 'The total budget for the campaign' },
    { name: 'team', description: 'The structure of the team (e.g., Small, Medium, Large, Agency, In-house, Hybrid)' },
    { name: 'brand_position', description: 'The brand\'s market position (e.g., Leader, Challenger, Disruptor, Newcomer)' },
    { name: 'timeline', description: 'The timeline for the campaign (e.g., Concepts by [date], in-market by [date])' },
    { name: 'theme', description: 'The theme of the campaign (e.g., "Innovation", "Experience", "Value")' },
    { name: 'brandvoice', description: 'The brand voice of the campaign (e.g., "Bold", "Playful", "Professional")' }
  ],
  'logo_test': [
    { name: 'goal', description: 'The objective of the logo test' },
    { name: 'image_descriptions', description: 'Descriptions of the logos being tested' },
  ],
  // Add empty arrays for prompt types that don't have specific variables
  'sales-enablement': [],
  'create-persona': [],
  'expert-reflection': [],
  'perplexity': [],
  // Healthcare prompts variables
  'healthcare-selection-prompt': [
    { name: 'query', description: 'User\'s research question' },
    { name: 'conversation_context', description: 'Chat history (if any)' },
    { name: 'respondent_list', description: 'JSON list of available respondents with id, name, bio, tags' }
  ],
  'healthcare-system-prompt': [], // No variables - twin-specific data added programmatically
  'healthcare-query-prompt': [
    { name: 'query', description: 'User\'s question' },
    { name: 'name', description: 'Respondent name' },
    { name: 'persona_json', description: 'Full persona profile data as JSON (for self-selection eligibility checks)' }
  ],
  'healthcare-consolidation-prompt': [
    { name: 'query', description: 'Research question' },
    { name: 'respondent_intro', description: 'Respondent backgrounds' },
    { name: 'responses_text', description: 'All respondent perspectives' }
  ],
  // Natural Organic Shoppers prompts variables
  'natural-organic-system-prompt': [], // No variables - twin-specific data added programmatically
  'natural-organic-query-prompt': [
    { name: 'query', description: 'User\'s question' },
    { name: 'name', description: 'Respondent name' },
    { name: 'persona_json', description: 'Full persona profile data as JSON (for self-selection eligibility checks)' }
  ],
  'natural-organic-consolidation-prompt': [
    { name: 'query', description: 'Research question' },
    { name: 'respondent_intro', description: 'Respondent backgrounds' },
    { name: 'responses_text', description: 'All respondent perspectives' }
  ]
};

interface ServiceConfig {
  service: string;
  'selected-models': SelectedModels;
}

interface Config {
  prompts: PromptConfig;
  service_config: ServiceConfig;
}

// Available models by provider
const availableModels = {
  claude: ["claude-sonnet-4-6","claude-3-opus-20240229", "claude-3-7-sonnet-20250219", "claude-3-haiku-2024n0307", 'claude-opus-4-20250514', 'claude-sonnet-4-20250514', 'claude-opus-4-1-20250805', 'claude-sonnet-4-5-20250929', 'claude-opus-4-5', 'claude-haiku-4-5'],
  openai: ["gpt-4o", "o4-mini-2025-04-16", "o3-2025-04-16", "o3-mini-2025-01-31", "o1-2024-12-17", "gpt-5-2025-08-07", "gpt-5-mini-2025-08-07", "gpt-5-nano-2025-08-07", "gpt-4.1-2025-04-14", 'gpt-5.2', 'gpt-5.2-pro', 'gpt-5.1'],
  gemini: ['gemini-3.1-pro-preview', 'gemini-2.5-pro', 'gemini-2.5-flash-preview-05-20', 'gemini-3.5-flash'],
  deepseek: ["deepseek-chat", "deepseek-reasoner"],
  groq: ['meta-llama/llama-4-scout-17b-16e-instruct', 'meta-llama/llama-4-maverick-17b-128e-instruct', 'llama-3.2-90b-vision-preview', "llama-3.1-8b-instant", 'llama-3.3-70b-specdec', 'llama-3.3-70b-versatile']
};

const AdminPanel: React.FC = () => {
  // UI State
  const [activeTab, setActiveTab] = useState<'dashboard' | 'config' | 'users' | 'enterprise' | 'freeTier'>('dashboard');
  const [dashboardView, setDashboardView] = useState<'executive' | 'technical'>('executive');
  const [selectedPromptType, setSelectedPromptType] = useState<string>('survey_and_focus_groups');
  const [variablesVisible, setVariablesVisible] = useState<{[key: string]: boolean}>({});
  
  // Data State
  const [config, setConfig] = useState<Config | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  
  // Loading/Error States
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  
  // Subscription Management State
  const [editingPlan, setEditingPlan] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  
  // Initialize variables visibility state for each prompt type
  useEffect(() => {
    // Set initial visibility to true for all prompt types
    const initialVisibility: {[key: string]: boolean} = {};
    Object.keys(PROMPT_VARIABLES).forEach(promptType => {
      initialVisibility[promptType] = true;
    });
    setVariablesVisible(initialVisibility);
  }, []);

  // Fetch config and users when component mounts
  useEffect(() => {
    fetchConfig();
    fetchUsers();
  }, []);

  // Clear action message after 3 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => {
        setActionMessage(null);
        setSaveSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/config`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }
      
      const data = await response.json();
      
      // Process the response to handle constant names in the API response
      if (data.prompts) {
        // Check for constant name keys and map them to actual keys
        Object.keys(data.prompts).forEach(key => {
          if (PROMPT_CONSTANTS_MAP[key]) {
            // Map from constant name to actual key
            const actualKey = PROMPT_CONSTANTS_MAP[key];
            data.prompts[actualKey] = data.prompts[key];
          }
        });
      }
      
      setConfig(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching configuration:', err);
      setError('Failed to load configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    
    try {
      setSaving(true);

      // Create a copy of the config for API submission
      const apiConfig = JSON.parse(JSON.stringify(config));
      
      // Map string keys to constant names for the API
      if (apiConfig.prompts) {
        // Save single string prompts with constant name keys
        if (apiConfig.prompts[CREATE_PERSONA_PROMPT]) {
          apiConfig.prompts['CREATE_PERSONA_PROMPT'] = apiConfig.prompts[CREATE_PERSONA_PROMPT];
          delete apiConfig.prompts[CREATE_PERSONA_PROMPT];
        }
        
        if (apiConfig.prompts[EXPERT_REFLECTION_PROMPT]) {
          apiConfig.prompts['EXPERT_REFLECTION_PROMPT'] = apiConfig.prompts[EXPERT_REFLECTION_PROMPT];
          delete apiConfig.prompts[EXPERT_REFLECTION_PROMPT];
        }
        
        if (apiConfig.prompts[PERPLEXITY_PROMPT]) {
          apiConfig.prompts['PERPLEXITY_PROMPT'] = apiConfig.prompts[PERPLEXITY_PROMPT];
          delete apiConfig.prompts[PERPLEXITY_PROMPT];
        }
      }
      
      const response = await fetch(`${API_URL}/admin/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(apiConfig)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }
      
      setSaveSuccess(true);
      setActionMessage('Configuration saved successfully!');
    } catch (err) {
      console.error('Error saving configuration:', err);
      setSaveSuccess(false);
      setActionMessage('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const toggleAdminStatus = async (userId: number, makeAdmin: boolean) => {
    try {
      const endpoint = makeAdmin ? 'make-admin' : 'revoke-admin';
      const response = await fetch(`${API_URL}/admin/users/${userId}/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.detail || 'Failed to update user status');
      }
      
      // Update local users state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, is_admin: makeAdmin } : user
      ));
      
      setSaveSuccess(true);
      setActionMessage(`User ${makeAdmin ? 'promoted to admin' : 'demoted from admin'} successfully!`);
    } catch (err) {
      console.error('Error updating user status:', err);
      setSaveSuccess(false);
      setActionMessage(`Failed to ${makeAdmin ? 'promote' : 'demote'} user: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const updateUserSubscription = async (userId: number, newPlan: string) => {
    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ plan: newPlan, reset_limits: false })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.detail || 'Failed to update subscription');
      }
      
      // Refresh users list
      await fetchUsers();
      
      setSaveSuccess(true);
      setActionMessage(`Subscription updated successfully!`);
      setEditingPlan(null);
    } catch (err) {
      console.error('Error updating subscription:', err);
      setSaveSuccess(false);
      setActionMessage(`Failed to update subscription: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const resetUserLimits = async (userId: number) => {
    try {
      const response = await fetch(`${API_URL}/admin/users/${userId}/reset-limits`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.detail || 'Failed to reset limits');
      }
      
      // Refresh users list
      await fetchUsers();
      
      setSaveSuccess(true);
      setActionMessage(`Usage limits reset successfully!`);
    } catch (err) {
      console.error('Error resetting limits:', err);
      setSaveSuccess(false);
      setActionMessage(`Failed to reset limits: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const getPlanBadgeStyles = (plan: string) => {
    switch(plan) {
      case 'free':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'starter':
        return 'bg-[#E8FCFA] text-[#06D4C2] border-[#07E5D1]/20';
      case 'growth':
        return 'bg-[#07E5D1]/10 text-[#06D4C2] border-[#07E5D1]';
      case 'enterprise':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'complementary':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'internal':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusBadgeStyles = (status: string) => {
    switch(status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700';
      case 'cancelled':
        return 'bg-amber-50 text-amber-700';
      case 'expired':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const formatPlanName = (plan: string) => {
    if (plan === 'complementary') return 'Complementary';
    if (plan === 'internal') return 'Internal';
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const handlePromptChange = (
    promptType: string, 
    index: number, 
    newValue: string,
    promptField: 'prompts' | 'service_config' = 'prompts'
  ) => {
    if (!config) return;
    
    setConfig({
      ...config,
      [promptField]: {
        ...config[promptField],
        [promptType]: config[promptField][promptType].map((prompt: string, i: number) => 
          i === index ? newValue : prompt
        )
      }
    });
  };

  const handleDeepResearchToggle = (value: boolean) => {
    if (!config) return;

    setConfig({
      ...config,
      prompts: {
        ...config.prompts,
        DEEP_RESEARCH: value
      }
    });
  };

  const handleMemoryToggle = (value: boolean) => {
    if (!config) return;

    setConfig({
      ...config,
      prompts: {
        ...config.prompts,
        MEMORY: value
      }
    });
  };

  const handleModelChange = (provider: string, model: string) => {
    if (!config) return;
    
    setConfig({
      ...config,
      service_config: {
        ...config.service_config,
        'selected-models': {
          ...config.service_config['selected-models'],
          [provider]: model
        }
      }
    });
  };

  const handleAIServiceChange = (service: string) => {
    if (!config) return;
    
    // When changing the AI service, also set the default model for that service
    const defaultModel = availableModels[service as keyof typeof availableModels]?.[0] || "";
    
    setConfig({
      ...config,
      service_config: {
        ...config.service_config,
        service
      }
    });
  };

  const handleAddEmptyPrompt = (promptType: string) => {
    if (!config) return;
    
    setConfig({
      ...config,
      prompts: {
        ...config.prompts,
        [promptType]: [...(config.prompts[promptType as keyof typeof config.prompts] || []), ""]
      }
    });
  };

// Function to determine if a prompt type should be handled as a string (single value) or array (multiple prompts)
  const isSingleStringPromptType = (promptType: string): boolean => {
    return [
      'create-persona', 
      'expert-reflection', 
      'perplexity'
    ].includes(promptType);
  };

  const renderVariablesReference = (promptType: string, phaseIndex?: number) => {
    let variables: any[] = [];
    let phaseLabel = '';
    
    // Special handling for test_use_case with phases
    if (promptType === 'test_use_case' && phaseIndex !== undefined) {
      const testUseCaseVars = PROMPT_VARIABLES[promptType as keyof typeof PROMPT_VARIABLES];
      if (typeof testUseCaseVars === 'object' && 'phase1' in testUseCaseVars) {
        switch(phaseIndex) {
          case 0:
            variables = testUseCaseVars.phase1;
            phaseLabel = ' - Research Phase';
            break;
          case 1:
            variables = testUseCaseVars.phase2;
            phaseLabel = ' - Persona Reaction Phase';
            break;
          case 2:
            variables = testUseCaseVars.phase3;
            phaseLabel = ' - Summary Phase';
            break;
          default:
            variables = testUseCaseVars.phase1;
        }
      }
    } else {
      // Regular handling for other prompt types
      const varsConfig = PROMPT_VARIABLES[promptType as keyof typeof PROMPT_VARIABLES];
      if (Array.isArray(varsConfig)) {
        variables = varsConfig;
      }
    }
    
    if (variables.length === 0) {
      return null;
    }
    
    // Use the component-level state for visibility
    const visibilityKey = phaseIndex !== undefined ? `${promptType}_phase${phaseIndex}` : promptType;
    const isVisible = variablesVisible[visibilityKey] !== undefined ? variablesVisible[visibilityKey] : true;
    
    const toggleVisibility = () => {
      setVariablesVisible(prev => ({
        ...prev,
        [visibilityKey]: !isVisible
      }));
    };
    
    return (
      <div className="mb-4 bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
        <div 
          className="px-4 py-2 bg-gray-100 flex justify-between items-center cursor-pointer"
          onClick={toggleVisibility}
        >
          <h4 className="text-sm font-medium text-gray-700">
            Available Variables{phaseLabel}
          </h4>
          <button className="text-gray-500 hover:text-gray-700">
            {isVisible ? '▼' : '►'}
          </button>
        </div>
        
        {isVisible && (
          <div className="p-4">
            <p className="text-xs text-gray-500 mb-2">
              You can use these variables in your prompt by enclosing them in {'${ }'}.
              For example: <code className="bg-gray-100 px-1 rounded">{`\${example_variable}`}</code>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {variables.map((variable: any) => (
                <div key={variable.name} className="border border-gray-200 rounded p-2 bg-white">
                  <div className="font-mono text-sm text-blue-600">
                    {`\${${variable.name}}`}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {variable.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPromptEditor = () => {
    if (!config) return null;
    
    // Group prompt types into categories
    const simulationPromptTypes = [
      // { id: 'attribution', label: 'Attribution' },
      { id: 'create-content', label: 'Create Content' },
      { id: 'create-content-long', label: 'Create Long Form Content' },
      { id: 'a/b-multivariate-tests', label: 'A/B & Multivariate Tests' },
      // { id: 'get-insights', label: 'Get Insights' },
      // { id: 'pricing-analysis', label: 'Pricing Analysis' },
      // { id: 'ab-test-creatives-gemini', label: 'A/B Test Creatives, Ads' },
      { id: 'packaging-review-gemini', label: 'Packaging Review (Gemini)' },
      // { id: 'buyer-insights-report', label: 'Customer Insights Report' },
      { id: 'buyer-insights-report-b2b', label: 'B2B Customer Insights Report' },
      { id: 'buyer-insights-report-b2c', label: 'B2C Customer Insights Report' },
     { id: 'channel-event-strategy', label: 'A/B Test Creatives' },
     { id: 'image-survey', label: 'Image Survey' },
      { id: 'creative-review', label: 'Creative Review' },
      { id: 'survey_and_focus_groups', label: 'Survey' },
      { id: 'create_campaign_strategy', label: 'Create Campaign Strategy' },
      { id: 'amazon-search', label: 'Amazon Search' },
      { id: 'logo_test', label: 'Logo Test' },
      { id: 'test_use_case', label: 'Test Use Case' }
    ];
    
    // Build audience prompt types (single string prompts)
    const audiencePromptTypes = [
      { id: CREATE_PERSONA_PROMPT, label: 'Create Persona' },
      { id: EXPERT_REFLECTION_PROMPT, label: 'Expert Reflection' },
      { id: PERPLEXITY_PROMPT, label: 'Perplexity' }
    ];
    
    // Ask Healthcare Chat - single page with all prompts
    const HEALTHCARE_CHAT_ID = 'ask-healthcare-chat';
    const healthcarePromptKeys = [
      { id: 'healthcare-selection-prompt', label: 'Step 1: Expert Selection', description: 'AI selects relevant respondents based on query' },
      { id: 'healthcare-system-prompt', label: 'Step 2a: System Prompt', description: 'Base persona identity for each twin', note: 'Twin-specific data (name, bio, hospital info, etc.) is automatically appended to this prompt.' },
      { id: 'healthcare-query-prompt', label: 'Step 2b: Query Prompt', description: 'User query format sent to each twin' },
      { id: 'healthcare-consolidation-prompt', label: 'Step 3: Consolidation', description: 'AI synthesizes all responses into final answer' }
    ];
    
    // Ask Natural Organic Shoppers - single page with all prompts
    const NATURAL_ORGANIC_CHAT_ID = 'ask-natural-organic-chat';
    const naturalOrganicPromptKeys = [
      { id: 'natural-organic-system-prompt', label: 'Step 1: System Prompt', description: 'Base persona identity for each shopper twin', note: 'Twin-specific data (name, segment, bio, shopping profile) is automatically appended to this prompt.' },
      { id: 'natural-organic-query-prompt', label: 'Step 2: Query Prompt', description: 'User query format sent to each twin with self-selection' },
      { id: 'natural-organic-consolidation-prompt', label: 'Step 3: Consolidation', description: 'AI synthesizes all responses into final answer' }
    ];
    
    // Check if healthcare chat is selected
    const isHealthcareChatSelected = selectedPromptType === HEALTHCARE_CHAT_ID;
    const isNaturalOrganicChatSelected = selectedPromptType === NATURAL_ORGANIC_CHAT_ID;
    
    // Get the currently selected AI provider
    const selectedProvider = config.service_config.service;
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex space-x-6">
            <div className="space-x-2">
              <label className="text-sm font-medium text-gray-700">Deep Research:</label>
              <div className="inline-flex relative">
                <button
                  onClick={() => handleDeepResearchToggle(true)}
                  className={`px-3 py-1 text-sm rounded-l-md ${
                    config.prompts.DEEP_RESEARCH
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => handleDeepResearchToggle(false)}
                  className={`px-3 py-1 text-sm rounded-r-md ${
                    !config.prompts.DEEP_RESEARCH
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Off
                </button>
              </div>
            </div>

            <div className="space-x-2">
              <label className="text-sm font-medium text-gray-700">Memory Layer:</label>
              <div className="inline-flex relative">
                <button
                  onClick={() => handleMemoryToggle(true)}
                  className={`px-3 py-1 text-sm rounded-l-md ${
                    config.prompts.MEMORY
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  On
                </button>
                <button
                  onClick={() => handleMemoryToggle(false)}
                  className={`px-3 py-1 text-sm rounded-r-md ${
                    !config.prompts.MEMORY
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Off
                </button>
              </div>
            </div>
          </div>

          <div className="relative inline-block">
            <label className="text-sm font-medium text-gray-700 mr-2">AI Provider:</label>
            <select
              value={config.service_config.service}
              onChange={(e) => handleAIServiceChange(e.target.value)}
              className="py-1 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="claude">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
              <option value="deepseek">DeepSeek</option>
              <option value="groq">Groq</option>
			  <option value="gemini">Gemini</option>
            </select>
          </div>
        </div>
        
        <div className="flex gap-4">
          <div className="w-1/4 pr-4 border-r">
            <h3 className="font-medium text-gray-700 mb-4">Prompt Categories</h3>
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-500">Simulation Prompts</h4>
              {simulationPromptTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setSelectedPromptType(type.id)}
                  className={`w-full text-left px-3 py-2 rounded-md ${
                    selectedPromptType === type.id
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {type.label}
                </button>
              ))}
              <h4 className="text-sm font-medium text-gray-500 mt-4">Build Audience Prompts</h4>
              {audiencePromptTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setSelectedPromptType(type.id)}
                  className={`w-full text-left px-3 py-2 rounded-md ${
                    selectedPromptType === type.id
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {type.label}
                </button>
              ))}
              
              <h4 className="text-sm font-medium text-gray-500 mt-4">Ask Healthcare</h4>
              <button
                onClick={() => setSelectedPromptType(HEALTHCARE_CHAT_ID)}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  isHealthcareChatSelected
                    ? 'bg-[#E8FCFA] text-[#06D4C2] font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Ask Healthcare Chat
              </button>
              
              <h4 className="text-sm font-medium text-gray-500 mt-4">Ask Natural Organic</h4>
              <button
                onClick={() => setSelectedPromptType(NATURAL_ORGANIC_CHAT_ID)}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  isNaturalOrganicChatSelected
                    ? 'bg-[#E8FCFA] text-[#06D4C2] font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Ask Natural Organic Chat
              </button>
            </div>
          </div>
          
          <div className="w-3/4">
            <div className="mb-4 flex justify-between items-center">
              <h3 className="font-medium text-gray-700">
                {isHealthcareChatSelected 
                  ? 'Ask Healthcare Chat Prompts'
                  : isNaturalOrganicChatSelected
                  ? 'Ask Natural Organic Shoppers Prompts'
                  : (simulationPromptTypes.concat(audiencePromptTypes).find(t => t.id === selectedPromptType)?.label || 'Prompts')}
              </h3>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={saveConfig}
                disabled={saving}
                icon={saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              >
                Save Changes
              </Button>
            </div>
            
            {/* Add Variables Reference here - only for non-test_use_case and non-healthcare and non-natural-organic */}
            {selectedPromptType !== 'test_use_case' && !isHealthcareChatSelected && !isNaturalOrganicChatSelected && renderVariablesReference(selectedPromptType)}
            
            {/* Healthcare Chat - show all prompts on one page */}
            {isHealthcareChatSelected ? (
              <div className="space-y-6">
                {/* Workflow description */}
                <div className="p-4 bg-[#E8FCFA] border border-[#07E5D1]/30 rounded-lg">
                  <h4 className="font-medium text-[#06D4C2] mb-2">Ask Healthcare Chat Workflow</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p><span className="font-semibold text-[#06D4C2]">Step 1:</span> Expert Selection - AI selects relevant respondents based on query</p>
                    <p><span className="font-semibold text-[#06D4C2]">Step 2:</span> Individual Expert Query - Each selected twin responds using system + query prompts</p>
                    <p><span className="font-semibold text-[#06D4C2]">Step 3:</span> Consolidation - AI synthesizes all responses into final answer</p>
                  </div>
                </div>
                
                {/* All 4 healthcare prompts stacked */}
                {healthcarePromptKeys.map((promptConfig) => (
                  <div key={promptConfig.id} className="border border-gray-300 rounded-md">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                      <h4 className="text-sm font-semibold text-gray-800">{promptConfig.label}</h4>
                      <p className="text-xs text-gray-500 mt-1">{promptConfig.description}</p>
                      {promptConfig.note && (
                        <p className="text-xs text-[#06D4C2] mt-1 italic">{promptConfig.note}</p>
                      )}
                    </div>
                    {/* Variables reference for this prompt */}
                    {renderVariablesReference(promptConfig.id)}
                    <textarea 
                      value={String(config.prompts[promptConfig.id as keyof typeof config.prompts] || '')}
                      onChange={(e) => {
                        setConfig({
                          ...config,
                          prompts: {
                            ...config.prompts,
                            [promptConfig.id]: e.target.value
                          }
                        });
                      }}
                      className="w-full p-3 min-h-[180px] text-sm font-mono resize-y border-t-0"
                      placeholder={`Enter ${promptConfig.label.toLowerCase()} prompt here...`}
                    />
                  </div>
                ))}
              </div>
            ) : isNaturalOrganicChatSelected ? (
              <div className="space-y-6">
                {/* Workflow description */}
                <div className="p-4 bg-[#E8FCFA] border border-[#07E5D1]/30 rounded-lg">
                  <h4 className="font-medium text-[#06D4C2] mb-2">Ask Natural Organic Shoppers Workflow</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p><span className="font-semibold text-[#06D4C2]">Step 1:</span> System Prompt - Base persona identity for each shopper twin</p>
                    <p><span className="font-semibold text-[#06D4C2]">Step 2:</span> Query with Self-Selection - Each twin determines eligibility and responds</p>
                    <p><span className="font-semibold text-[#06D4C2]">Step 3:</span> Consolidation - AI synthesizes all eligible responses into final answer</p>
                  </div>
                </div>
                
                {/* All 3 natural organic prompts stacked */}
                {naturalOrganicPromptKeys.map((promptConfig) => (
                  <div key={promptConfig.id} className="border border-gray-300 rounded-md">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                      <h4 className="text-sm font-semibold text-gray-800">{promptConfig.label}</h4>
                      <p className="text-xs text-gray-500 mt-1">{promptConfig.description}</p>
                      {promptConfig.note && (
                        <p className="text-xs text-[#06D4C2] mt-1 italic">{promptConfig.note}</p>
                      )}
                    </div>
                    {/* Variables reference for this prompt */}
                    {renderVariablesReference(promptConfig.id)}
                    <textarea 
                      value={String(config.prompts[promptConfig.id as keyof typeof config.prompts] || '')}
                      onChange={(e) => {
                        setConfig({
                          ...config,
                          prompts: {
                            ...config.prompts,
                            [promptConfig.id]: e.target.value
                          }
                        });
                      }}
                      className="w-full p-3 min-h-[180px] text-sm font-mono resize-y border-t-0"
                      placeholder={`Enter ${promptConfig.label.toLowerCase()} prompt here...`}
                    />
                  </div>
                ))}
              </div>
            ) : (
            <div className="space-y-4">
              {isSingleStringPromptType(selectedPromptType) ? (
                // Render single textarea for string prompts (create-persona, expert-reflection, perplexity)
                <div className="border border-gray-300 rounded-md">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 flex justify-between items-center">
                    <h4 className="text-sm font-medium">Prompt</h4>
                  </div>
                  <textarea 
                    value={config.prompts[selectedPromptType] || ''}
                    onChange={(e) => {
                      setConfig({
                        ...config,
                        prompts: {
                          ...config.prompts,
                          [selectedPromptType]: e.target.value
                        }
                      });
                    }}
                    className="w-full p-3 min-h-[200px] text-sm font-mono resize-y"
                    placeholder="Enter prompt here..."
                  />
                </div>
              ) : (
                // Array-based prompts (2 or 3 prompts depending on task type)
                (() => {
                  // Determine if this is a three-phase task
                  const isThreePhaseTask = THREE_PHASE_TASKS.includes(selectedPromptType);
                  const promptCount = isThreePhaseTask ? 3 : 2;
                  const promptIndices = Array.from({ length: promptCount }, (_, i) => i);
                  
                  // Get labels for prompts based on task type
                  const getPromptLabel = (index: number) => {
                    if (isThreePhaseTask) {
                      switch(index) {
                        case 0: return 'Research Phase Prompt';
                        case 1: return 'Persona Reaction Prompt';
                        case 2: return 'Summary Phase Prompt';
                        default: return `Prompt ${index + 1}`;
                      }
                    } else {
                      return index === 0 ? 'Run Simulation Prompt' : 'Advanced Simulation Prompt';
                    }
                  };
                  
                  return promptIndices.map((index) => {
                    // Get current prompts array for the selected type
                    const promptsArray = config.prompts[selectedPromptType as keyof typeof config.prompts] || [];
                    // Get the prompt value, or empty string if it doesn't exist
                    const promptValue = promptsArray[index] !== undefined ? promptsArray[index] : '';
                    
                    return (
                      <div key={index} className="space-y-2">
                        {/* Show phase-specific variables for test_use_case */}
                        {selectedPromptType === 'test_use_case' && renderVariablesReference(selectedPromptType, index)}
                        
                        <div className="border border-gray-300 rounded-md">
                          <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 flex justify-between items-center">
                            <h4 className="text-sm font-medium">
                              {getPromptLabel(index)}
                            </h4>
                            {isThreePhaseTask && (
                              <span className="text-xs text-gray-500">
                                {index === 0 ? 'Initial analysis' : index === 1 ? 'Persona evaluations' : 'Final synthesis'}
                              </span>
                            )}
                          </div>
                          <textarea 
                          value={promptValue}
                          onChange={(e) => {
                            // If the array doesn't have this index yet, we need to initialize it
                            if (index >= promptsArray.length) {
                              // Create a new array with empty strings up to this index
                              const newPromptsArray = [...promptsArray];
                              while (newPromptsArray.length <= index) {
                                newPromptsArray.push('');
                              }
                              // Set the value at the current index
                              newPromptsArray[index] = e.target.value;
                              
                              // Update the entire config
                              setConfig({
                                ...config,
                                prompts: {
                                  ...config.prompts,
                                  [selectedPromptType]: newPromptsArray
                                }
                              });
                            } else {
                              // Just use the normal handler if the index exists
                              handlePromptChange(selectedPromptType, index, e.target.value);
                            }
                          }}
                          className="w-full p-3 min-h-[200px] text-sm font-mono resize-y"
                          placeholder={isThreePhaseTask 
                            ? (index === 0 ? 'Enter research prompt with ${variables}...' 
                              : index === 1 ? 'Enter persona reaction prompt with ${variables}...'
                              : 'Enter summary prompt with ${variables}...')
                            : 'Enter prompt here...'}
                        />
                        </div>
                      </div>
                    );
                  });
                })()
              )}
            </div>
            )}
          </div>
        </div>
        
        <div className="mt-8 border-t pt-6">
          <h3 className="font-medium text-gray-700 mb-4">Selected Models</h3>
          <div className="grid grid-cols-1 gap-4">
            {/* Active provider's model selection (dropdown) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {selectedProvider === 'claude' ? 'Anthropic (Claude)' :
                selectedProvider === 'openai' ? 'OpenAI (GPT)' :
                selectedProvider === 'deepseek' ? 'DeepSeek' :
                selectedProvider === 'groq' ? 'Groq' : 
				selectedProvider === 'gemini' ? 'Gemini' :'Selected Model'}
              </label>
              <select
                value={config.service_config['selected-models'][selectedProvider]}
                onChange={(e) => handleModelChange(selectedProvider, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                {availableModels[selectedProvider as keyof typeof availableModels]?.map((model) => (
                  <option key={model} value={model}>
                    {model}
                  </option>
                ))}
              </select>
            </div>

            {/* All providers model configuration (read-only when not active) */}
            <div className="mt-4 grid grid-cols-1 gap-6">
              <h4 className="text-sm font-medium text-gray-500">All Configured Models</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className={`block text-sm font-medium ${selectedProvider === 'claude' ? 'text-blue-700' : 'text-gray-500'}`}>
                    Anthropic (Claude)
                  </label>
                  <select
                    value={config.service_config['selected-models'].claude}
                    onChange={(e) => handleModelChange('claude', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm 
                    ${selectedProvider === 'claude' 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'}`}
                    disabled={selectedProvider !== 'claude'}
                  >
                    {availableModels.claude.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-medium ${selectedProvider === 'openai' ? 'text-blue-700' : 'text-gray-500'}`}>
                    OpenAI (GPT)
                  </label>
                  <select
                    value={config.service_config['selected-models'].openai}
                    onChange={(e) => handleModelChange('openai', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm
                    ${selectedProvider === 'openai' 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'}`}
                    disabled={selectedProvider !== 'openai'}
                  >
                    {availableModels.openai.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
				<div className="space-y-2">
                  <label className={`block text-sm font-medium ${selectedProvider === 'gemini' ? 'text-blue-700' : 'text-gray-500'}`}>
                    Gemini
                  </label>
                  <select
                    value={config.service_config['selected-models'].gemini}
                    onChange={(e) => handleModelChange('gemini', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm
                    ${selectedProvider === 'gemini' 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'}`}
                    disabled={selectedProvider !== 'gemini'}
                  >
                    {availableModels.gemini.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-medium ${selectedProvider === 'deepseek' ? 'text-blue-700' : 'text-gray-500'}`}>
                    DeepSeek
                  </label>
                  <select
                    value={config.service_config['selected-models'].deepseek}
                    onChange={(e) => handleModelChange('deepseek', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm
                    ${selectedProvider === 'deepseek' 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'}`}
                    disabled={selectedProvider !== 'deepseek'}
                  >
                    {availableModels.deepseek.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-medium ${selectedProvider === 'groq' ? 'text-blue-700' : 'text-gray-500'}`}>
                    Groq
                  </label>
                  <select
                    value={config.service_config['selected-models'].groq}
                    onChange={(e) => handleModelChange('groq', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm
                    ${selectedProvider === 'groq' 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-300 bg-gray-50 text-gray-500'}`}
                    disabled={selectedProvider !== 'groq'}
                  >
                    {availableModels.groq.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUserManagement = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-gray-700">User Management</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchUsers}
            icon={<RefreshCw className="w-4 h-4 mr-1" />}
          >
            Refresh
          </Button>
        </div>
        
        <div className="bg-white shadow-md rounded-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">
                        {user.email}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {user.id}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {user.is_admin ? (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                        Admin
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        User
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {editingPlan === user.id ? (
                      <div className="flex items-center space-x-2">
                        <select
                          value={selectedPlan}
                          onChange={(e) => setSelectedPlan(e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="free">Free</option>
                          <option value="starter">Starter</option>
                          <option value="growth">Growth</option>
                          <option value="enterprise">Enterprise</option>
                          <option value="complementary">Complementary</option>
                          <option value="internal">Internal</option>
                        </select>
                        <button
                          onClick={() => updateUserSubscription(user.id, selectedPlan)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingPlan(null)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-md border ${getPlanBadgeStyles(user.subscription_plan)}`}>
                          {formatPlanName(user.subscription_plan)}
                          {user.subscription_plan === 'growth' && (
                            <span className="ml-1 text-[10px] bg-[#07E5D1] text-white px-1 rounded">Popular</span>
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setEditingPlan(user.id);
                            setSelectedPlan(user.subscription_plan);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeStyles(user.subscription_status)}`}>
                      {user.subscription_status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center text-xs text-gray-600">
                        <TrendingUp className="w-3 h-3 mr-1" />
                        <span>
                          {user.monthly_simulation_count}/{user.simulations_limit === -1 ? '∞' : user.simulations_limit} simulations
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {user.total_audience_requests} audience requests
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-2">
                      <div className="flex justify-center space-x-2">
                        {user.is_admin ? (
                          <button
                            onClick={() => toggleAdminStatus(user.id, false)}
                            className="text-xs text-red-600 hover:text-red-900"
                          >
                            Revoke Admin
                          </button>
                        ) : (
                          <button
                            onClick={() => toggleAdminStatus(user.id, true)}
                            className="text-xs text-blue-600 hover:text-blue-900"
                          >
                            Make Admin
                          </button>
                        )}
                      </div>
                      <div className="flex justify-center">
                        <button
                          onClick={() => resetUserLimits(user.id)}
                          className="flex items-center text-xs text-amber-600 hover:text-amber-900"
                        >
                          <RotateCcw className="w-3 h-3 mr-1" />
                          Reset Usage
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Shield className="h-6 w-6 mr-2 text-blue-600" />
          Admin Panel
        </h1>
        <p className="text-gray-500 mt-1">
          Configure system settings and manage users
        </p>
      </div>
      
      {/* Action message (success/error) */}
      {actionMessage && (
        <div className={`mb-4 p-3 rounded-md ${
          saveSuccess ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        } flex items-center justify-between`}>
          <div className="flex items-center">
            {saveSuccess ? (
              <Check className="h-5 w-5 mr-2" />
            ) : (
              <X className="h-5 w-5 mr-2" />
            )}
            <p>{actionMessage}</p>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-6 inline-flex items-center text-sm font-medium border-b-2 ${
                activeTab === 'dashboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BarChart3 className="mr-2 h-5 w-5" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`py-4 px-6 inline-flex items-center text-sm font-medium border-b-2 ${
                activeTab === 'config'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="mr-2 h-5 w-5" />
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 px-6 inline-flex items-center text-sm font-medium border-b-2 ${
                activeTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="mr-2 h-5 w-5" />
              User Management
            </button>
            <button
              onClick={() => setActiveTab('enterprise')}
              className={`py-4 px-6 inline-flex items-center text-sm font-medium border-b-2 ${
                activeTab === 'enterprise'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="mr-2 h-5 w-5" />
              Enterprise Usage
            </button>
            <button
              onClick={() => setActiveTab('freeTier')}
              className={`py-4 px-6 inline-flex items-center text-sm font-medium border-b-2 ${
                activeTab === 'freeTier'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <UserRoundCog className="mr-2 h-5 w-5" />
              Free Tier Users
            </button>
          </nav>
        </div>
        
        {/* Tab content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              <p>{error}</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => activeTab === 'config' ? fetchConfig() : fetchUsers()}
                icon={<RefreshCw className="w-4 h-4 mr-1" />}
              >
                Try Again
              </Button>
            </div>
          ) : (
            activeTab === 'dashboard' ? (
              <div className="space-y-4">
                {/* Dashboard View Toggle */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
                    <button
                      onClick={() => setDashboardView('executive')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        dashboardView === 'executive'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Eye className="w-4 h-4" />
                      Executive View
                    </button>
                    <button
                      onClick={() => setDashboardView('technical')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        dashboardView === 'technical'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Code className="w-4 h-4" />
                      Technical View
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    {dashboardView === 'executive'
                      ? 'Comprehensive business intelligence and strategic metrics'
                      : 'Detailed LLM usage analytics and technical metrics'}
                  </p>
                </div>

                {/* Render selected dashboard */}
                {dashboardView === 'executive' ? (
                  <CEODashboard />
                ) : (
                  <UnifiedAnalyticsDashboard />
                )}
              </div>
            ) : activeTab === 'config' ? (
              renderPromptEditor()
            ) : activeTab === 'enterprise' ? (
              <EnterpriseDashboard />
            ) : activeTab === 'freeTier' ? (
              <FreeTierDashboard />
            ) : (
              renderUserManagement()
            )
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminPanel;